import React, { useEffect, useState } from 'react';
import useApiStable from '../../utils/useApiStable';
import { useAuth } from '../../context/AuthContext';
import { canManageClubEvents } from '../../utils/userUtils';
import ClubEventForm from './ClubEventForm';
import '../../styles/TaskList.css';

function formatDateTime(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatDeadline(value) {
  if (!value) return '';
  const [y, m, d] = value.split('-');
  return `${d}.${m}.${y}`;
}

function ParticipantsModal({ appointmentId, onClose }) {
  const api = useApiStable();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/appointments/${appointmentId}/participants`)
      .then(data => setParticipants(Array.isArray(data) ? data : []))
      .catch(err => setError(err.message || 'Fehler beim Laden der Teilnehmerliste'))
      .finally(() => setLoading(false));
  }, [appointmentId]); // eslint-disable-line

  const confirmed = participants.filter(p => p.status === 'CONFIRMED');
  const declined = participants.filter(p => p.status === 'DECLINED');

  return (
    <div className="taskform-card">
      <h3 style={{ marginBottom: '18px' }}>Zu-/Absagen</h3>
      {error && <div className="taskform-error">{error}</div>}
      {loading ? <p>Lade...</p> : (
        <>
          <p><strong>Zugesagt ({confirmed.length})</strong></p>
          <ul className="tasklist-list">
            {confirmed.map(p => <li className="tasklist-item" key={p.personId}>{p.personName}</li>)}
            {confirmed.length === 0 && <li className="tasklist-item">Noch niemand.</li>}
          </ul>
          <p style={{ marginTop: 16 }}><strong>Abgesagt ({declined.length})</strong></p>
          <ul className="tasklist-list">
            {declined.map(p => <li className="tasklist-item" key={p.personId}>{p.personName}</li>)}
            {declined.length === 0 && <li className="tasklist-item">Niemand.</li>}
          </ul>
        </>
      )}
      <div className="taskform-actions">
        <button type="button" className="taskform-cancel-btn" onClick={onClose}>Schließen</button>
      </div>
    </div>
  );
}

function ClubEventsPage() {
  const { user } = useAuth();
  const api = useApiStable();
  const manage = canManageClubEvents(user);

  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const [respondingId, setRespondingId] = useState(null);
  const [participantsEventId, setParticipantsEventId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const loadEvents = () => {
    setError('');
    api.get('/appointments/club-events')
      .then(data => setEvents(Array.isArray(data) ? data : []))
      .catch(err => setError(err.message || 'Fehler beim Laden der Termine'));
  };

  useEffect(() => {
    loadEvents();
  }, []); // eslint-disable-line

  const handleRespond = (id, status) => {
    setRespondingId(id);
    setError('');
    api.post(`/appointments/${id}/respond`, { status })
      .then(loadEvents)
      .catch(err => setError(err.message || 'Fehler beim Speichern der Antwort'))
      .finally(() => setRespondingId(null));
  };

  const handleFormClose = (changed) => {
    setShowForm(false);
    setSelectedEvent(null);
    if (changed) loadEvents();
  };

  return (
    <div className="tasklist-container">
      <div className="tasklist-header">
        <h2>Vereinstermine</h2>
        {manage && <button className="tasklist-btn" onClick={() => { setSelectedEvent(null); setShowForm(true); }}>Termin anlegen</button>}
      </div>
      {error && <div style={{ color: '#c62828', fontSize: '0.85em', marginBottom: '8px' }}>{error}</div>}
      <ul className="tasklist-list">
        {events.map(e => (
          <li className="tasklist-item" key={e.id} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <div className="tasklist-item-info">
              <strong>{e.title}</strong> — {formatDateTime(e.startTime)}
              {e.location && <><br /><span style={{ color: '#666', fontSize: '0.9em' }}>Ort: {e.location}</span></>}
              {e.description && <><br /><span style={{ color: '#666', fontSize: '0.95em' }}>{e.description}</span></>}
              <br />
              <span style={{ color: '#666', fontSize: '0.9em' }}>
                {e.confirmedCount} zugesagt · {e.declinedCount} abgesagt
              </span>
              {e.registrationDeadline && (
                <span style={{ marginLeft: 8, fontSize: '0.85em', color: e.registrationClosed ? '#c62828' : '#666' }}>
                  {e.registrationClosed ? 'Anmeldefrist abgelaufen' : `Anmeldefrist: ${formatDeadline(e.registrationDeadline)}`}
                </span>
              )}
              {e.myStatus && (
                <span style={{ marginLeft: 8, fontSize: '0.85em', color: e.myStatus === 'CONFIRMED' ? '#2e7d32' : '#e65100' }}>
                  (Meine Antwort: {e.myStatus === 'CONFIRMED' ? 'Zugesagt' : 'Abgesagt'})
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {e.registrationDeadline && (
                <>
                  <button className="tasklist-edit-btn" disabled={respondingId === e.id || e.registrationClosed}
                          onClick={() => handleRespond(e.id, 'CONFIRMED')}>Zusagen</button>
                  <button className="tasklist-edit-btn" style={{ background: '#888' }} disabled={respondingId === e.id || e.registrationClosed}
                          onClick={() => handleRespond(e.id, 'DECLINED')}>Absagen</button>
                </>
              )}
              <button className="tasklist-edit-btn" style={{ background: '#35424a' }}
                      onClick={() => setParticipantsEventId(e.id)}>Teilnehmer</button>
              {manage && (
                <button className="tasklist-edit-btn" style={{ background: '#35424a' }}
                        onClick={() => { setSelectedEvent(e); setShowForm(true); }}>Bearbeiten</button>
              )}
            </div>
          </li>
        ))}
        {events.length === 0 && <li className="tasklist-item">Aktuell keine anstehenden Vereinstermine.</li>}
      </ul>
      {showForm && <ClubEventForm event={selectedEvent} onClose={handleFormClose} />}
      {participantsEventId && (
        <ParticipantsModal appointmentId={participantsEventId} onClose={() => setParticipantsEventId(null)} />
      )}
    </div>
  );
}

export default ClubEventsPage;
